/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Card } from "@material-ui/core";
import { getContactFlowsAsync, getPhoneNumbersAsync } from "../services/AmazonConnectService";
import { getCallMeNowConfigsAsync, updateCallMeNowConfigAsync } from "../services/CallMeNowConfigService";
import errorCodes from "../../../utility/errorCodes";
import { v2, errorToObject } from "@prftesp/cle-logger";
import {
	ContentLayout,
	LeftPanelLayout,
	PanelContainer,
	ProgressOverlay,
	RightPanelLayout,
	ShowSnackbar,
} from "../../../components/Common";
import { ConfigurationsList } from "../components/ConfigurationsList";
import ConfigurationDetailsCard from "../components/ConfigurationDetailsCard";
import { useCallback } from "react";
import { useEffect } from "react";

const COMPONENT_NAME = "Admin callMeNowManager/pages/CallMeNowConfigurationPage";

const verifyConfig = (config) => {
	var validConfig =
		config &&
		config.allowedOrigins &&
		config.allowedOrigins.replace(/\s/g, "").length > 0 &&
		config.contactFlowId &&
		config.contactFlowId.length > 0 &&
		config.configName &&
		config.configName.replace(/\s/g, "").length > 0 &&
		config.sourcePhoneNumber &&
		config.sourcePhoneNumber.length > 0;

	var validOptions =
		config &&
		((config.enabledCallOptions === true &&
			config.subjects &&
			config.subjects.length > 0 &&
			config.subjects.filter((sub) => sub.subject === "").length <= 0) ||
			config.enabledCallOptions === false);
	return validConfig && validOptions;
};

const CallMeNowConfigurationPage = () => {
	const [contactFlows, setContactFlows] = React.useState(undefined);
	const [phoneNumbers, setPhoneNumbers] = React.useState(undefined);
	const [callMeNowConfigs, setConfigs] = React.useState(undefined);
	const [selectedConfig, setSelectedConfig] = React.useState(undefined);
	const [selectedConfigId, setSelectedConfigId] = React.useState(undefined);
	const [notification, setNotification] = React.useState({ message: "", severity: "success" });
	const [edited, setEdited] = React.useState(false);
	const [validationMessage, setValidationMessage] = React.useState(undefined);
	const isValidConfig = verifyConfig(selectedConfig);
	const [isLoading, setIsLoading] = React.useState(false);

	const handleSnackBarClose = () => {
		setNotification((cur) => ({ ...cur, message: "" }));
	};

	const getConfigs = useCallback(async (resetSelection = true) => {
		setIsLoading(true);
		const newConfigs = await getCallMeNowConfigsAsync();
		setConfigs(newConfigs || []);
		if (resetSelection) {
			setSelectedConfigId(undefined);
			setSelectedConfig(undefined);
		}
		setIsLoading(false);
	}, []);

	//get contact flows
	const getContactFlows = useCallback(async () => {
		setIsLoading(true);
		const flows = await getContactFlowsAsync();
		setContactFlows(flows);
		setIsLoading(false);
	}, []);

	//get phone numbers
	const getPhoneNumbers = useCallback(async () => {
		setIsLoading(true);
		const phoneNumbers = await getPhoneNumbersAsync();
		setPhoneNumbers(phoneNumbers);
		setIsLoading(false);
	}, []);

	useEffect(() => {
		getContactFlows();
		getPhoneNumbers();
		getConfigs(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateCallMeNowConfig = async (newConfig, successMessage, failureMessage) => {
		setIsLoading(true);
		try {
			setSelectedConfig(newConfig.config);
			setSelectedConfigId(newConfig.callMeNowConfigId);
			await updateCallMeNowConfigAsync({
				updateCallMeNowConfig: newConfig,
			});
			setNotification({ message: successMessage, severity: "success" });
			await getConfigs(false);
			setEdited(false);
		} catch (error) {
			v2.error(`Error updating call me now config`, {
				additionalData: {
					error: errorToObject(error),
					data: { selectedConfig, selectedConfigId },
				},
				logCode: errorCodes.UPDATE_CALLMENOW_CONFIG_ERROR,
				componentName: COMPONENT_NAME,
			});
			setNotification({ message: failureMessage, severity: "error" });
		} finally {
			setIsLoading(false);
		}
	};

	const handleSelectedConfigChange = (newConfig) => {
		setEdited(false);
		setSelectedConfig(newConfig.config);
		setSelectedConfigId(newConfig.callMeNowConfigId);
	};

	return (
		<ContentLayout style={{ maxWidth: "100%" }} className="w-100">
			{isLoading && <ProgressOverlay />}
			<ShowSnackbar severity={notification.severity} message={notification.message} onClose={handleSnackBarClose} />
			<PanelContainer>
				<LeftPanelLayout style={{ maxWidth: 408 }} className="w-30">
					<Card style={{ minHeight: 600, height: "100%", padding: "10px 5px" }}>
						<ConfigurationsList
							configurations={callMeNowConfigs}
							addConfigurationHandler={updateCallMeNowConfig}
							formEdited={edited}
							selectConfigurationHandler={handleSelectedConfigChange}
							selectedConfiguration={selectedConfig}
							selectedConfigId={selectedConfigId}
						/>
					</Card>
				</LeftPanelLayout>
				<RightPanelLayout className="w-70">
					<Card>
						<ConfigurationDetailsCard
							selectedConfig={selectedConfig}
							setIsLoading={setIsLoading}
							edited={edited}
							contactFlows={contactFlows}
							setEdited={setEdited}
							selectedConfigId={selectedConfigId}
							setSelectedConfig={setSelectedConfig}
							validationMessage={validationMessage}
							setValidationMessage={setValidationMessage}
							setNotification={setNotification}
							refreshConfigs={getConfigs}
							isCurrentConfigValid={isValidConfig}
							updateCallMeNowConfig={updateCallMeNowConfig}
							callMeNowConfigs={callMeNowConfigs}
							phoneNumbers={phoneNumbers}
						/>
					</Card>
				</RightPanelLayout>
			</PanelContainer>
		</ContentLayout>
	);
};

export default CallMeNowConfigurationPage;
